<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch, watchEffect } from 'vue';
import { Tooltip } from 'ant-design-vue';
import type { IEllipsis } from '../types';

interface IProps extends IEllipsis {
  maxLength?: number;
  value: string;
  symbol?: string;
  scroll?: boolean;
  scrollSpeed?: number;
  // scrollDuration?: number;
}

let props = withDefaults(defineProps<IProps>(), {
  maxLength: 20,
  value: '',
  symbol: '.',
  scroll: false,
  scrollSpeed: 50,
});

const isOver = ref(false); // 是否超出指定宽度
const showValue = computed(() => {
  let showValue = '';
  if (props.value && props.value.length && props.value.length > props.maxLength) {
    showValue = props.value.substring(0, props.maxLength) + props.symbol.repeat(3);
  } else {
    showValue = props.value;
  }
  return showValue;
});

const scrollableTextRef = ref<HTMLElement | null>(null);

const textContainerRef = ref<Element | null>(null);

const showValueRef = ref<HTMLElement | null>(null);

const isScrolling = ref(false);
const scrollDuration = ref(0);

watchEffect(async () => {
  isOver.value = props.value.length > props.maxLength;
  if (scrollableTextRef.value && isOver.value && props.scroll) {
    const textContainer = textContainerRef.value as Element;
    const observer = new IntersectionObserver(
      ([entry]) => {
        isScrolling.value = entry.intersectionRatio < 1;
      },
      { threshold: [1] }
    );
    if (textContainer) observer.observe(textContainer);
  }
});

watch(
  [() => props.value, () => props.scrollSpeed],
  () => {
    isScrolling.value = false;
    if (scrollableTextRef.value) {
      const textContainer = textContainerRef.value;
      textContainer?.scrollTo(0, 0);
    }
    nextTick(() => {
      scrollDuration.value = (props.value.length * props.scrollSpeed) / 1000;
      const dom = document.createElement('div');
      dom.setAttribute('class', 'text-content');
      dom.setAttribute('style', 'opacity:0;width: fit-content');
      dom.innerText = showValue.value;
      document.body.append(dom);
      const { width } = dom?.getBoundingClientRect() || {};
      if (scrollableTextRef.value) scrollableTextRef.value.style.width = `${width}px`;

      dom.remove();
    });
  },
  { immediate: true }
);

// const scrollDuration = computed(() => props.scrollDuration * 1000);

// const scrollTextStyle = computed(() => ({
//   animationDuration: `${scrollDuration.value}s`,
// }));

onMounted(() => {});
</script>

<template>
  <div v-if="scroll" ref="scrollableTextRef" class="scrollable-text">
    <div
      ref="textContainerRef"
      class="text-container"
      :class="{ scrolling: isScrolling }"
      :style="{ animationDuration: `${scrollDuration}s` }"
    >
      <div class="text-content">{{ value }}</div>
      <div class="text-content">{{ value }}</div>
    </div>
  </div>
  <template v-else>
    <span v-if="!isOver" class="spanItem">{{ value }} </span>
    <Tooltip v-else>
      <template #title>
        {{ value }}
      </template>
      <span ref="showValueRef">{{ showValue }} </span>
    </Tooltip>
  </template>
</template>

<style scoped>
.scrollable-text {
  overflow-x: hidden;
  white-space: nowrap;
}

.text-container {
  display: inline-block;
  transition: transform 0.3s ease-out;
}

.text-container.scrolling {
  animation: scrolling-text linear infinite;
}

.text-content {
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 1rem; /* 添加间距，以防止文本连接时出现突兀感 */
}

@keyframes scrolling-text {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-100%);
  }
}
</style>
